<script lang="ts">
    import { onMount } from 'svelte';
    import { api } from '../lib/api';
    import { envs } from '../lib/stores/environment.svelte';
    import Link from '../components/Link.svelte';
    import type { Consumer, Subscription, AuditLog } from '../lib/types';
    import { formatRelative } from '../lib/format';

    let consumers = $state<Consumer[]>([]);
    let subs = $state<Subscription[]>([]);
    let recent = $state<AuditLog[]>([]);
    let loading = $state(true);

    async function load() {
        loading = true;
        try {
            const [c, s, a] = await Promise.all([
                api<Consumer[]>('/api/consumers'),
                api<Subscription[]>('/api/subscriptions'),
                api<{ items: AuditLog[] }>('/api/audit?limit=15'),
            ]);
            consumers = c;
            subs = s;
            recent = a.items;
        } finally {
            loading = false;
        }
    }
    onMount(load);

    function cellFor(consumerId: string, envId: string) {
        return subs.find((s) => s.consumerId === consumerId && s.environmentId === envId);
    }

    function statusLevel(sub?: Subscription): 'ok' | 'warn' | 'fail' | 'none' {
        if (!sub) return 'none';
        if (sub.lastPingStatus === 'SUCCESS') return 'ok';
        if (sub.lastPingStatus === 'FAILED') return 'fail';
        return 'warn';
    }
</script>

<div class="page-header">
    <h1>Dashboard</h1>
    <button onclick={load}>Refresh</button>
</div>

{#if loading}
    <p class="muted">Loading...</p>
{:else}
    <div class="grid">
        <section class="card">
            <h2>App Sync Matrix</h2>
            <p class="muted">Webhook health of each app in each environment</p>

            {#if consumers.length === 0}
                <p class="muted">No apps registered yet.</p>
            {:else if envs.list.length === 0}
                <p class="muted">No environments configured yet.</p>
            {:else}
                <table class="matrix">
                    <thead>
                    <tr>
                        <th></th>
                        {#each envs.list as e}
                            <th style="color: {e.themeColor};">{e.name}</th>
                        {/each}
                    </tr>
                    </thead>
                    <tbody>
                    {#each consumers as c}
                        <tr>
                            <td class="app-name">{c.name}</td>
                            {#each envs.list as e}
                                {@const cell = cellFor(c.id, e.id)}
                                {@const lvl = statusLevel(cell)}
                                <td>
                                    <span class="dot {lvl}" title={cell?.callbackUrl ?? 'Not registered'}></span>
                                    {#if cell?.lastPingAt}
                                        <span class="ts">{formatRelative(cell.lastPingAt)}</span>
                                    {/if}
                                </td>
                            {/each}
                        </tr>
                    {/each}
                    </tbody>
                </table>
                <div class="legend">
                    <span><span class="dot ok"></span> Healthy</span>
                    <span><span class="dot fail"></span> Failing</span>
                    <span><span class="dot warn"></span> Pending</span>
                    <span><span class="dot none"></span> Not registered</span>
                </div>
            {/if}
        </section>

        <section class="card">
            <h2>Recent Activity</h2>
            <ul class="feed">
                {#each recent as log}
                    <li>
                        <div class="action">{log.action}</div>
                        <div class="muted">
                            {log.actorLabel}{log.target ? ` → ${log.target}` : ''}
                        </div>
                        <div class="dim">{formatRelative(log.timestamp)}</div>
                    </li>
                {:else}
                    <li class="muted">No activity yet.</li>
                {/each}
            </ul>
            <Link to="/audit">View full audit log →</Link>
        </section>
    </div>
{/if}

<style>
    .page-header {
        display: flex; justify-content: space-between; align-items: center;
        margin-bottom: 20px;
    }
    .grid {
        display: grid;
        grid-template-columns: 2fr 1fr;
        gap: 20px;
    }
    table.matrix {
        width: 100%; border-collapse: collapse; margin-top: 12px;
    }
    table.matrix th, table.matrix td {
        padding: 10px 12px; text-align: center;
        border-bottom: 1px solid var(--border);
    }
    table.matrix th { font-size: 12px; text-transform: uppercase; }
    .app-name { text-align: left !important; font-weight: 600; }
    .dot {
        display: inline-block; width: 10px; height: 10px; border-radius: 50%;
        vertical-align: middle;
    }
    .dot.ok   { background: var(--success); box-shadow: 0 0 8px var(--success); }
    .dot.warn { background: var(--warning); }
    .dot.fail { background: var(--danger);  box-shadow: 0 0 8px var(--danger); }
    .dot.none { background: var(--bg-3); }
    .ts { font-size: 11px; color: var(--text-3); margin-left: 6px; }
    .legend {
        display: flex; gap: 16px; margin-top: 12px;
        font-size: 12px; color: var(--text-2);
    }
    .legend span { display: inline-flex; align-items: center; gap: 6px; }
    .feed { list-style: none; padding: 0; margin: 12px 0; max-height: 500px; overflow-y: auto; }
    .feed li { padding: 10px 0; border-bottom: 1px solid var(--border); font-size: 13px; }
    .action { font-family: var(--mono); font-size: 11px; color: var(--accent); }
</style>